import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Building } from 'models/Building'
import useSWR from 'swr'
import { Point } from 'types/building'
import Krpano from 'utils/krpano'

import useTourViewerKrpano from '../hook/Krpano/useTourViewerKrpano'

/**
 * This component is a part of the tour viewer page.
 * It handles the panorama viewer part of the page.
 * It gets the point detail from the API and sets the initial properties of the Krpano viewer.
 * It also handles the case when the user selects a different point.
 */
export default function TourViewerPanorama() {
  const { id } = useParams()
  const {
    currentPov,
    currentViewer,
    setCurrentViewer,
    setCurrentHgap,
    embedpano
  } = useTourViewerKrpano()

  const [searchParams] = useSearchParams()
  const hlookat = searchParams.get('hlookat')
  const vlookat = searchParams.get('vlookat')
  const fov = searchParams.get('fov')

  useEffect(() => {
    ;(window as any).setKrpanoLoadingStatus = (status: any) => {
      const normalized = status === true || status === 'true'
      if (!normalized) {
        setTimeout(() => {
          setCurrentViewer((prev) => ({
            ...prev,
            imageIsLoading: normalized
          }))
        }, 700)
      } else {
        setCurrentViewer((prev) => ({
          ...prev,
          imageIsLoading: normalized
        }))
      }
    }

    return () => {
      delete (window as any).setKrpanoLoadingStatus
    }
  }, [setCurrentViewer])

  const { data: buildingInfo } = useSWR<Building>({
    url: `v1/buildings/${id}`
  })

  // get point detail when select point
  useSWR<Point>(
    currentViewer.selectedFloor && currentViewer.selectedPoint
      ? {
          url: `v1/points/${currentViewer.selectedPoint}`,
          params: {
            building_id: id,
            floor_id: currentViewer?.selectedFloor
          }
        }
      : null,
    {
      onSuccess: (data) => {
        setCurrentViewer((prev) => ({
          ...prev,
          pointDetail: data,
          selectedImage: data.image360?.id,
          selectedFloor: data.floorId
        }))
        if (data.image360) {
          const pov = currentPov || data.initView
          const initProps = {
            imageUrl: data.image360.s3Path,
            previewUrl: data.image360?.multires
              ? `${data.image360?.tilesUrl}preview.jpg`
              : data.image360?.thumbnail,
            hgap: data.initView.radarHGap,
            h: hlookat ? +hlookat : pov.h,
            v: vlookat ? +vlookat : pov.v,
            z: fov ? +fov : pov.z,
            multires: data.image360.multires,
            tilesUrl: data.image360.tilesUrl
          }
          setCurrentHgap(initProps.hgap)
          const annotationList = data.annotations?.filter(
            (item) => !item.imageID || item.imageID === data.image360?.id
          )

          const stairList = data.stairways?.map((item) => ({
            ...item,
            nextFloorName: buildingInfo?.floors.find(
              (floor) => floor.id === +item.nextFloorId
            )?.name
          }))

          embedpano(initProps, annotationList, data.directions, stairList, data)
        } else {
          Krpano.UnmountPanoViewer()
        }
      }
    }
  )

  return <div id="viewer-pano-container" className="w-full h-full" />
}
